import React, { useState } from "react";
import { Helmet } from "react-helmet-async";

// /apps/agentpager/get — the short "how do I actually start using this" page.
//
// AgentPager is the rare app where installing it is only half the job: the phone is useless until
// the little program on your computer is running. Every support question we expect starts there, so
// this page gives the two steps equal weight instead of a lone Play badge that leaves people stuck
// on a scanner screen. No auto-redirect: a page that bounces you to the Play Store before you have
// read step 2 is how people end up thinking the app is broken.

const PLAY = "https://play.google.com/store/apps/details?id=com.cosmovex.mdpilot";
const CMD = "npx agentpager";

// Contrast checked against IVORY #FBFAF6: INK #1A2233 = 14.6:1, SUB #5A6678 = 5.4:1,
// VIOLET #6D28D9 = 7.4:1. White on VIOLET = 7.4:1. All clear WCAG AA.
const VIOLET = "#6D28D9";
const PLUM = "#4C1D95";
const INK = "#1A2233";
const SUB = "#5A6678";
const IVORY = "#FBFAF6";
const CARD = "#FFFFFF";
const LINE = "#E8E4F0";
const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `"SF Mono", "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace`;

const Step: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <li style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 28 }}>
    <span
      aria-hidden="true"
      style={{
        flex: "0 0 auto",
        width: 34,
        height: 34,
        borderRadius: 17,
        background: VIOLET,
        color: "#fff",
        fontWeight: 800,
        fontSize: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {n}
    </span>
    <div style={{ minWidth: 0 }}>
      <h2 style={{ margin: "4px 0 6px", fontSize: 20, fontWeight: 800, color: INK }}>{title}</h2>
      <div style={{ color: SUB, fontSize: 16, lineHeight: 1.55 }}>{children}</div>
    </div>
  </li>
);

const AgentPagerGet: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    // Clipboard access can be refused (insecure context, permissions); never leave a button that
    // silently does nothing — fall back to selecting the text so it can still be copied by hand.
    navigator.clipboard?.writeText(CMD).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => setCopied(false),
    );
  };

  return (
    <main style={{ background: IVORY, minHeight: "100vh", fontFamily: SANS, color: INK }}>
      <Helmet>
        <title>Get AgentPager — run your coding agents from your phone</title>
        <meta
          name="description"
          content="Install AgentPager on Android, run npx agentpager on your computer, scan the QR code. Two steps, no account, end-to-end encrypted."
        />
        <link rel="canonical" href="https://cosmovexapps.com/apps/agentpager/get" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Get AgentPager" />
        <meta
          property="og:description"
          content="Install the app, run npx agentpager on your computer, scan the code. Your code never leaves your computer."
        />
        <meta property="og:url" content="https://cosmovexapps.com/apps/agentpager/get" />
        <meta property="og:image" content="https://cosmovexapps.com/images/agentpager/feature.webp" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <section style={{ maxWidth: 720, margin: "0 auto", padding: "56px 20px 72px" }}>
        <header style={{ textAlign: "center", marginBottom: 40 }}>
          <img
            src="/images/play/agentpager.png"
            alt="AgentPager app icon"
            width={72}
            height={72}
            style={{ borderRadius: 18, boxShadow: "0 6px 20px rgba(76,29,149,0.18)" }}
          />
          <h1 style={{ fontSize: 34, lineHeight: 1.15, fontWeight: 900, margin: "18px 0 10px", color: INK }}>
            Get AgentPager
          </h1>
          <p style={{ fontSize: 17, color: SUB, margin: 0, lineHeight: 1.5 }}>
            Two steps: the app on your phone, and one command on the computer your agents run on.
          </p>
        </header>

        <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
          <Step n={1} title="Install the app">
            <p style={{ margin: "0 0 14px" }}>Android 8.0 or newer. Free, with an optional Pro upgrade.</p>
            <a
              href={PLAY}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: VIOLET,
                color: "#fff",
                fontWeight: 800,
                fontSize: 16,
                padding: "13px 22px",
                borderRadius: 12,
                textDecoration: "none",
              }}
            >
              Get it on Google Play
            </a>
          </Step>

          <Step n={2} title="Start AgentPager on your computer">
            <p style={{ margin: "0 0 12px" }}>
              This is the part people miss. Open a terminal on the computer where Claude Code, Codex,
              Gemini CLI or OpenCode already run, and paste:
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                background: "#12101C",
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 12,
              }}
            >
              <code style={{ fontFamily: MONO, fontSize: 15.5, color: "#E9E4FF" }}>{CMD}</code>
              <button
                type="button"
                onClick={copy}
                style={{
                  background: copied ? "#1F9254" : "#2A2540",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p style={{ margin: 0 }}>
              Needs Node 20 or newer. Works on macOS, Linux and Windows. Nothing to configure, no
              ports to open, and it never asks for an account.
            </p>
          </Step>

          <Step n={3} title="Scan the code it shows">
            <p style={{ margin: 0 }}>
              A QR code appears in the terminal. Tap <strong>Scan the code</strong> in the app and
              point your phone at it. Your sessions show up a few seconds later. The code is
              single-use and expires after 10 minutes — if you have paired that computer before, ask
              it for a fresh one with <code style={{ fontFamily: MONO }}>npx agentpager pair</code>.
            </p>
          </Step>
        </ol>

        <aside
          style={{
            background: CARD,
            border: `1px solid ${LINE}`,
            borderRadius: 16,
            padding: 20,
            marginTop: 8,
          }}
        >
          <h2 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 800, color: PLUM }}>
            What it can and cannot see
          </h2>
          <p style={{ margin: 0, color: SUB, fontSize: 15.5, lineHeight: 1.55 }}>
            Your code never leaves your computer. Your phone and that computer talk end-to-end
            encrypted, and the relay in between cannot open the messages — it deletes each one once
            it is delivered. To let you pick a project from your phone, the computer sends it a list
            of folder <em>names</em>, never file contents, and only from inside your home folder or
            beside a project you already use. There is no account and no sign-up.
          </p>
        </aside>

        <nav style={{ marginTop: 34, textAlign: "center", fontSize: 15 }}>
          <a href="/apps/agentpager" style={{ color: VIOLET, fontWeight: 700, textDecoration: "none" }}>
            What AgentPager does
          </a>
          <span style={{ color: LINE, margin: "0 10px" }}>·</span>
          <a href="/apps/agentpager/support" style={{ color: VIOLET, fontWeight: 700, textDecoration: "none" }}>
            Support
          </a>
          <span style={{ color: LINE, margin: "0 10px" }}>·</span>
          <a href="/apps/agentpager/privacy-policy" style={{ color: VIOLET, fontWeight: 700, textDecoration: "none" }}>
            Privacy
          </a>
        </nav>
      </section>
    </main>
  );
};

export default AgentPagerGet;
